import { Injectable, inject } from '@angular/core';
import { Observable, from, switchMap } from 'rxjs';
import { ApiService } from './api.service';
import { Order, OrderActorRef, ServingPolicy, TimeStats } from '@crowdctrl/core';

export interface OrderCopyItem {
  id: string;
  orderId: string;
  queueId: string;
  queueName?: string | null;
  sessionId: string;
  sessionSequence?: number | null;
  status: string;
  enqueuedAt: string;
  claimedBy?: OrderActorRef | null;
  summary?: string | null;
  customerIdentifier?: string | null;
  callingBy?: OrderActorRef | null;
  callingAt?: string | null;
  startedAt?: string | null;
  startedBy?: OrderActorRef | null;
  waitTimeMs?: number | null;
  completedAt?: string | null;
  completedBy?: OrderActorRef | null;
  serviceTimeMs?: number | null;
  turnaroundTimeMs?: number | null;
  queuePriority: number;
  /** Active serving policy id on the copy when resolved. */
  policyId?: string | null;
  policyQueuePriority?: number | null;
  /** Denormalized tenant user id of soft-claim / start owner; empty when unclaimed. */
  claimedByUserId?: string;
  /** When false, the responder's policy excludes this queue (copy still exists). */
  includedInServingPolicy?: boolean;
  policyResolvedAt?: string | null;
  /** Responder policy revision stamped when copy policy fields were last rebuilt. */
  policyVersion?: number | null;
  snoozedAt?: string | null;
  snoozedUntil?: string | null;
  snoozedBy?: OrderActorRef | null;
  snoozedId?: string | null;
  sourceUpdatedAt?: string | null;
  updatedAt?: string | null;
}

/** Canonical order row plus optional responder order copy (worklist / listener shape). */
export interface OrderMutationResponse {
  order: Order;
  orderCopy?: OrderCopyItem;
}

export interface OrderSearchFilters {
  queueId?: string | null;
  customerName?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
}

export interface OrderActionItem {
  id: string;
  type: string;
  occurredAt: string;
  /** Tie-break for same `occurredAt`; lower sorts first when listing newest-first. */
  sequence?: number;
  actor?: OrderActorRef | null;
  description: string;
  /**
   * Action-specific payload. The orders `.../actions` API may add display fields
   * (e.g. `queueName`, `fromQueueName`, `toQueueName`) while preserving ids.
   */
  details?: Record<string, any>;
  sincePreviousActionMs?: number | null;
}

/**
 * Service for order management API calls.
 */
@Injectable({
  providedIn: 'root',
})
export class OrderService {
  private apiService = inject(ApiService);
  private baseUrl: string = '';

  /**
   * Set the base URL for API calls (should be called from app initialization)
   */
  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  /**
   * Get orders for a session and optionally a specific queue.
   * Also fetches the serving policy if userId is provided.
   */
  getOrders(
    workspaceId: string,
    sessionId: string,
    queueId?: string,
    userId?: string,
    status?: string,
    limit?: number,
    cursor?: string,
  ): Observable<{
    orders: Order[];
    servingPolicy?: ServingPolicy;
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number | boolean> = {
      workspaceId,
      sessionId,
    };
    if (queueId) params['queueId'] = queueId;
    if (userId) params['userId'] = userId;
    if (status) params['status'] = status;
    if (limit) params['limit'] = limit;
    if (cursor) params['cursor'] = cursor;

    return from(
      this.apiService.get<{
        orders: Order[];
        servingPolicy?: ServingPolicy;
        pagination: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount: number;
          retrievedAt: string;
        };
      }>(this.baseUrl, `/orders_api/api/orders`, params),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get orders for a queue across all sessions in a workspace.
   */
  listQueueOrders(
    workspaceId: string,
    queueId: string,
    limit?: number,
    cursor?: string,
  ): Observable<{
    orders: Array<Order & { sessionName?: string; enqueuedAt?: string }>;
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number | boolean> = {
      workspaceId,
      queueId,
    };
    if (limit) params['limit'] = limit;
    if (cursor) params['cursor'] = cursor;

    return from(
      this.apiService.get<{
        orders: Array<Order & { sessionName?: string; enqueuedAt?: string }>;
        pagination: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount: number;
          retrievedAt: string;
        };
      }>(this.baseUrl, `/orders_api/api/orders`, params),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get responder work context: policies and responder state (no order copies).
   */
  getResponderWorkContext(
    workspaceId: string,
    sessionId: string,
  ): Observable<{
    servingPolicies: any[];
    responderState: {
      activePolicyId?: string | null;
      allowedPolicyIds?: string[];
      policyVersion?: number | null;
      orderCopyPolicySyncPending?: boolean;
      orderCopyPolicySyncLastError?: string | null;
      orderCopyPolicySyncProcessed?: number | null;
      orderCopyPolicySyncTotal?: number | null;
      /** Copies scanned but not updated (already at policy version or missing source order). */
      orderCopyPolicySyncSkipped?: number | null;
      orderCopyPolicySyncCloudTaskName?: string | null;
    };
    policyWorkload?: {
      count?: number;
      /** Per-queue qualifying copy counts (queue id → count). */
      byQueue?: Record<string, number>;
      determinedAt?: string | null;
      stale?: boolean;
      recomputeScheduledAt?: string | null;
      retrievedAt?: string;
      recomputeFailedAt?: string | null;
      recomputeError?: string | null;
    };
  }> {
    const params: Record<string, string | number | boolean> = {
      workspaceId,
      sessionId,
    };
    return from(
      this.apiService.get<{
        servingPolicies: any[];
        responderState: {
          activePolicyId?: string | null;
          allowedPolicyIds?: string[];
          policyVersion?: number | null;
          orderCopyPolicySyncPending?: boolean;
          orderCopyPolicySyncLastError?: string | null;
          orderCopyPolicySyncProcessed?: number | null;
          orderCopyPolicySyncTotal?: number | null;
          orderCopyPolicySyncSkipped?: number | null;
          orderCopyPolicySyncCloudTaskName?: string | null;
        };
        policyWorkload?: {
          count?: number;
          byQueue?: Record<string, number>;
          determinedAt?: string | null;
          stale?: boolean;
          recomputeScheduledAt?: string | null;
          retrievedAt?: string;
          recomputeFailedAt?: string | null;
          recomputeError?: string | null;
        };
      }>(this.baseUrl, `/orders_api/api/responders/me/work-context`, params),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get responder order copies. When `policyId` is omitted, returns copies for the session (all queues).
   */
  getResponderOrderCopies(
    workspaceId: string,
    sessionId: string,
    policyId?: string | null,
  ): Observable<{
    orderCopies: any[];
    pagination: { totalCount: number; retrievedAt: string };
  }> {
    const params: Record<string, string | number | boolean> = {
      workspaceId,
      sessionId,
    };
    if (policyId != null && policyId !== '') {
      params['policyId'] = policyId;
    }
    return from(
      this.apiService.get<{
        orderCopies: any[];
        pagination: { totalCount: number; retrievedAt: string };
      }>(this.baseUrl, `/orders_api/api/responders/me/order-copies`, params),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Per session member (responder/coordinator): copy presence, policy inclusion, serving rank for an order.
   */
  getOrderRespondersCopyDetails(
    workspaceId: string,
    sessionId: string,
    orderId: string,
  ): Observable<{
    items: Array<{
      userId: string;
      role: string;
      hasCopy: boolean;
      status: string | null;
      queueId: string | null;
      queueName: string | null;
      includedInServingPolicy: boolean | null;
      servingRank: number | null;
      inTopTen: boolean;
    }>;
    pagination: { totalCount: number; retrievedAt: string };
  }> {
    const params: Record<string, string | number | boolean> = {
      workspaceId,
      sessionId,
    };
    return from(
      this.apiService.get<{
        items: Array<{
          userId: string;
          role: string;
          hasCopy: boolean;
          status: string | null;
          queueId: string | null;
          queueName: string | null;
          includedInServingPolicy: boolean | null;
          servingRank: number | null;
          inTopTen: boolean;
        }>;
        pagination: { totalCount: number; retrievedAt: string };
      }>(this.baseUrl, `/orders_api/api/orders/${orderId}/responders-copy-details`, params),
    ).pipe(switchMap((obs) => obs));
  }

  getOrderActions(
    workspaceId: string,
    sessionId: string,
    orderId: string,
    limit?: number,
    cursor?: string,
  ): Observable<{
    items: OrderActionItem[];
    pagination: {
      totalCount: number;
      hasNext: boolean;
      nextCursor: string | null;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number | boolean> = {
      workspaceId,
      sessionId,
    };
    if (limit) params['limit'] = limit;
    if (cursor) params['cursor'] = cursor;
    return from(
      this.apiService.get<{
        items: OrderActionItem[];
        pagination: {
          totalCount: number;
          hasNext: boolean;
          nextCursor: string | null;
          retrievedAt: string;
        };
      }>(this.baseUrl, `/orders_api/api/orders/${orderId}/actions`, params),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get all responder user IDs that currently have a copy of an order.
   */
  getRespondersWithOrderCopy(
    workspaceId: string,
    sessionId: string,
    orderId: string,
  ): Observable<{
    responderIds: string[];
    pagination: { totalCount: number; retrievedAt: string };
  }> {
    const params: Record<string, string | number | boolean> = {
      workspaceId,
      sessionId,
    };
    return from(
      this.apiService.get<{
        responderIds: string[];
        pagination: { totalCount: number; retrievedAt: string };
      }>(this.baseUrl, `/orders_api/api/orders/${orderId}/responders-with-copy`, params),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Start a PENDING order (transition to IN_PROGRESS).
   */
  startOrder(
    workspaceId: string,
    sessionId: string,
    orderId: string,
  ): Observable<OrderMutationResponse> {
    return from(
      this.apiService.post<OrderMutationResponse, any>(
        this.baseUrl,
        `/orders_api/api/orders/${orderId}/start`,
        {
          workspaceId,
          sessionId,
        },
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Complete an IN_PROGRESS order (transition to COMPLETED).
   */
  completeOrder(
    workspaceId: string,
    sessionId: string,
    orderId: string,
  ): Observable<OrderMutationResponse> {
    return from(
      this.apiService.post<OrderMutationResponse, any>(
        this.baseUrl,
        `/orders_api/api/orders/${orderId}/complete`,
        {
          workspaceId,
          sessionId,
        },
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Cancel a PENDING or IN_PROGRESS order (transition to CANCELLED).
   */
  cancelOrder(
    workspaceId: string,
    sessionId: string,
    orderId: string,
    cancelReasonType: string,
    cancelReasonOtherDescription?: string,
  ): Observable<OrderMutationResponse> {
    const body: Record<string, string> = {
      workspaceId,
      sessionId,
      cancelReasonType,
    };
    if (cancelReasonOtherDescription) {
      body['cancelReasonOtherDescription'] = cancelReasonOtherDescription;
    }
    return from(
      this.apiService.post<OrderMutationResponse, Record<string, string>>(
        this.baseUrl,
        `/orders_api/api/orders/${orderId}/cancel`,
        body,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get aggregated time stats for a given scope.
   * staleByMetric and recomputeScheduledAtByMetric support per-row spinners.
   * When scheduleRecompute is true, backend enqueues a recompute (retry).
   * recomputeFailedAtByMetric and recomputeErrorByMetric are per-metric so each stat row can show its own Retry.
   */
  getTimeStats(
    workspaceId: string,
    sessionId?: string,
    queueId?: string,
    scheduleRecompute?: boolean,
  ): Observable<{
    stats?: TimeStats | null;
    stale?: boolean;
    recomputeScheduledAt?: string;
    updatedAt?: string;
    staleByMetric?: Record<string, boolean>;
    recomputeScheduledAtByMetric?: Record<string, string>;
    recomputeFailedAtByMetric?: Record<string, string>;
    recomputeErrorByMetric?: Record<string, string>;
  }> {
    const params: Record<string, string> = { workspaceId };
    if (sessionId) params['sessionId'] = sessionId;
    if (queueId) params['queueId'] = queueId;
    if (scheduleRecompute === true) params['scheduleRecompute'] = 'true';
    return from(
      this.apiService.get<{
        stats?: TimeStats | null;
        stale?: boolean;
        recomputeScheduledAt?: string;
        updatedAt?: string;
        staleByMetric?: Record<string, boolean>;
        recomputeScheduledAtByMetric?: Record<string, string>;
        recomputeFailedAtByMetric?: Record<string, string>;
        recomputeErrorByMetric?: Record<string, string>;
      }>(this.baseUrl, `/orders_api/api/orders/time-stats`, params),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get bucket distribution for a given scope and metric.
   * Reads from orderTimeStats shards; returns bucket counts and median.
   */
  getTimeStatsDistribution(
    workspaceId: string,
    metric: 'waitTime' | 'serviceTime' | 'turnaroundTime',
    sessionId?: string,
    queueId?: string,
  ): Observable<{
    buckets: number[];
    bucketBoundsMs: number[];
    totalCount: number;
    medianBucketLowMs: number | null;
    medianBucketHighMs: number | null;
    q1BucketLowMs: number | null;
    q1BucketHighMs: number | null;
    q3BucketLowMs: number | null;
    q3BucketHighMs: number | null;
    minMs: number | null;
    maxMs: number | null;
    avgMs: number | null;
    stale?: boolean;
    recomputeScheduledAt?: string | null;
    pendingByBucket?: Record<number, boolean>;
  }> {
    const params: Record<string, string> = { workspaceId, metric };
    if (sessionId) params['sessionId'] = sessionId;
    if (queueId) params['queueId'] = queueId;
    return from(
      this.apiService.get<{
        buckets: number[];
        bucketBoundsMs: number[];
        totalCount: number;
        medianBucketLowMs: number | null;
        medianBucketHighMs: number | null;
        q1BucketLowMs: number | null;
        q1BucketHighMs: number | null;
        q3BucketLowMs: number | null;
        q3BucketHighMs: number | null;
        minMs: number | null;
        maxMs: number | null;
        avgMs: number | null;
        stale?: boolean;
        recomputeScheduledAt?: string | null;
        pendingByBucket?: Record<number, boolean>;
      }>(this.baseUrl, `/orders_api/api/orders/time-stats/distribution`, params),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get wait time heat map for a session (time x wait buckets), one UTC calendar day.
   * Omit `day` to load the latest UTC day that has data.
   * Session-only; data is pre-aggregated via distributed counters.
   */
  getWaitTimeHeatmap(
    workspaceId: string,
    sessionId: string,
    day?: string | null,
  ): Observable<{
    timeLabels: string[];
    waitLabels: string[];
    rows: number[][];
    day: string | null;
    hasOlder: boolean;
    hasNewer: boolean;
  }> {
    const params: Record<string, string> = { workspaceId, sessionId };
    if (day != null && day !== '') {
      params['day'] = day;
    }
    return from(
      this.apiService.get<{
        timeLabels: string[];
        waitLabels: string[];
        rows: number[][];
        day: string | null;
        hasOlder: boolean;
        hasNewer: boolean;
      }>(this.baseUrl, `/orders_api/api/orders/time-stats/heatmap`, params),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get time stats for a session and all its queues in one request.
   * Backend discovers queue IDs from the session; frontend does not pass queue list.
   * When scheduleRecompute is true, backend enqueues recompute (retry).
   */
  getTimeStatsForSession(
    workspaceId: string,
    sessionId: string,
    scheduleRecompute?: boolean,
  ): Observable<{
    session: {
      stats?: TimeStats | null;
      stale?: boolean;
      recomputeScheduledAt?: string;
      updatedAt?: string;
      recomputeFailedAtByMetric?: Record<string, string>;
      recomputeErrorByMetric?: Record<string, string>;
    };
    queueControls: Array<{
      id: string;
      name: string;
      stats?: TimeStats | null;
      stale?: boolean;
      recomputeScheduledAt?: string;
      updatedAt?: string;
      recomputeFailedAtByMetric?: Record<string, string>;
      recomputeErrorByMetric?: Record<string, string>;
    }>;
  }> {
    const params: Record<string, string> = { workspaceId, sessionId };
    if (scheduleRecompute === true) params['scheduleRecompute'] = 'true';
    return from(
      this.apiService.get<{
        session: {
          stats?: TimeStats | null;
          stale?: boolean;
          recomputeScheduledAt?: string;
          updatedAt?: string;
          recomputeFailedAtByMetric?: Record<string, string>;
          recomputeErrorByMetric?: Record<string, string>;
        };
        queueControls: Array<{
          id: string;
          name: string;
          stats?: TimeStats | null;
          stale?: boolean;
          recomputeScheduledAt?: string;
          updatedAt?: string;
          recomputeFailedAtByMetric?: Record<string, string>;
          recomputeErrorByMetric?: Record<string, string>;
        }>;
      }>(this.baseUrl, `/orders_api/api/orders/time-stats/session`, params),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get sessions, queue instances, queue controls per session, and time stats in one request.
   * Each scope's time stats may include recomputeFailedAtByMetric and recomputeErrorByMetric for per-metric Retry.
   * Use for workspace Average Wait/Service/Turnaround Times page (single round-trip).
   */
  getTimeStatsWorkspaceOverview(
    workspaceId: string,
    scheduleRecompute?: boolean,
  ): Observable<{
    sessions: Array<{ id: string; name: string }>;
    queueInstances: Array<{ id: string; name: string }>;
    queueControlsBySession: Record<
      string,
      Array<{ id: string; name: string; queueInstanceId: string }>
    >;
    timeStats: {
      sessions: Record<
        string,
        {
          stats?: TimeStats | null;
          stale?: boolean;
          recomputeScheduledAt?: string;
          updatedAt?: string;
          recomputeFailedAtByMetric?: Record<string, string>;
          recomputeErrorByMetric?: Record<string, string>;
        }
      >;
      queues: Record<
        string,
        {
          stats?: TimeStats | null;
          stale?: boolean;
          recomputeScheduledAt?: string;
          updatedAt?: string;
          recomputeFailedAtByMetric?: Record<string, string>;
          recomputeErrorByMetric?: Record<string, string>;
        }
      >;
      /** Workspace-level queue stats (from .../queues/{queueId}/orderTimeStatsCurrent). No UI calculation. */
      workspaceQueues: Record<
        string,
        {
          stats?: TimeStats | null;
          stale?: boolean;
          recomputeScheduledAt?: string;
          updatedAt?: string;
          recomputeFailedAtByMetric?: Record<string, string>;
          recomputeErrorByMetric?: Record<string, string>;
        }
      >;
    };
  }> {
    const params: Record<string, string> = { workspaceId };
    if (scheduleRecompute === true) params['scheduleRecompute'] = 'true';
    return from(
      this.apiService.get<{
        sessions: Array<{ id: string; name: string }>;
        queueInstances: Array<{ id: string; name: string }>;
        queueControlsBySession: Record<
          string,
          Array<{ id: string; name: string; queueInstanceId: string }>
        >;
        timeStats: {
          sessions: Record<
            string,
            {
              stats?: TimeStats | null;
              stale?: boolean;
              recomputeScheduledAt?: string;
              updatedAt?: string;
              recomputeFailedAtByMetric?: Record<string, string>;
              recomputeErrorByMetric?: Record<string, string>;
            }
          >;
          queues: Record<
            string,
            {
              stats?: TimeStats | null;
              stale?: boolean;
              recomputeScheduledAt?: string;
              updatedAt?: string;
              recomputeFailedAtByMetric?: Record<string, string>;
              recomputeErrorByMetric?: Record<string, string>;
            }
          >;
          workspaceQueues: Record<
            string,
            {
              stats?: TimeStats | null;
              stale?: boolean;
              recomputeScheduledAt?: string;
              updatedAt?: string;
              recomputeFailedAtByMetric?: Record<string, string>;
              recomputeErrorByMetric?: Record<string, string>;
            }
          >;
        };
      }>(this.baseUrl, `/orders_api/api/orders/time-stats/workspace-overview`, params),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get time stats for many scopes in one request. Use for Average Wait/Service/Turnaround Times page.
   * Returns { sessions: { [sessionId]: payload }, queues: { [sessionId_queueId]: payload } }.
   */
  getTimeStatsBulk(
    workspaceId: string,
    sessionIds: string[],
    queueScopes: { sessionId: string; queueId: string }[],
  ): Observable<{
    sessions: Record<
      string,
      { stats?: TimeStats | null; stale?: boolean; recomputeScheduledAt?: string }
    >;
    queues: Record<
      string,
      { stats?: TimeStats | null; stale?: boolean; recomputeScheduledAt?: string }
    >;
  }> {
    const params: Record<string, string> = {
      workspaceId,
      sessionIds: sessionIds.join(','),
      queueScopes: queueScopes.map((s) => `${s.sessionId}:${s.queueId}`).join(','),
    };
    return from(
      this.apiService.get<{
        sessions: Record<
          string,
          { stats?: TimeStats | null; stale?: boolean; recomputeScheduledAt?: string }
        >;
        queues: Record<
          string,
          { stats?: TimeStats | null; stale?: boolean; recomputeScheduledAt?: string }
        >;
      }>(this.baseUrl, `/orders_api/api/orders/time-stats/bulk`, params),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Call an order (set callingBy flag).
   */
  callOrder(
    workspaceId: string,
    sessionId: string,
    orderId: string,
  ): Observable<OrderMutationResponse> {
    return from(
      this.apiService.post<OrderMutationResponse, any>(
        this.baseUrl,
        `/orders_api/api/orders/${orderId}/call`,
        {
          workspaceId,
          sessionId,
        },
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Stop calling an order (clear callingBy flag).
   */
  uncallOrder(
    workspaceId: string,
    sessionId: string,
    orderId: string,
  ): Observable<OrderMutationResponse> {
    return from(
      this.apiService.post<OrderMutationResponse, any>(
        this.baseUrl,
        `/orders_api/api/orders/${orderId}/uncall`,
        {
          workspaceId,
          sessionId,
        },
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Snooze a PENDING or IN_PROGRESS order for a duration.
   */
  snoozeOrder(
    workspaceId: string,
    sessionId: string,
    orderId: string,
    snoozeMinutes: number,
    expectedUpdatedAt?: string,
  ): Observable<OrderMutationResponse> {
    const body: Record<string, string | number> = {
      workspaceId,
      sessionId,
      snoozeMinutes,
    };
    if (expectedUpdatedAt) {
      body['expectedUpdatedAt'] = expectedUpdatedAt;
    }
    return from(
      this.apiService.post<OrderMutationResponse, Record<string, string | number>>(
        this.baseUrl,
        `/orders_api/api/orders/${orderId}/snooze`,
        body,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Resume (unsnooze) a snoozed order.
   */
  unsnoozeOrder(
    workspaceId: string,
    sessionId: string,
    orderId: string,
    expectedUpdatedAt?: string,
  ): Observable<OrderMutationResponse> {
    const body: Record<string, string> = {
      workspaceId,
      sessionId,
    };
    if (expectedUpdatedAt) {
      body['expectedUpdatedAt'] = expectedUpdatedAt;
    }
    return from(
      this.apiService.post<OrderMutationResponse, Record<string, string>>(
        this.baseUrl,
        `/orders_api/api/orders/${orderId}/unsnooze`,
        body,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Update order notes with notes-specific optimistic locking.
   */
  updateOrderNotes(
    workspaceId: string,
    sessionId: string,
    orderId: string,
    notes: string,
    expectedNotesVersion: number,
  ): Observable<OrderMutationResponse> {
    return from(
      this.apiService.post<
        OrderMutationResponse,
        {
          workspaceId: string;
          sessionId: string;
          notes: string;
          expectedNotesVersion: number;
        }
      >(this.baseUrl, `/orders_api/api/orders/${orderId}/notes`, {
        workspaceId,
        sessionId,
        notes,
        expectedNotesVersion,
      }),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Move an order to a different queue.
   */
  moveOrder(
    workspaceId: string,
    sessionId: string,
    orderId: string,
    targetQueueId: string,
  ): Observable<OrderMutationResponse> {
    return from(
      this.apiService.post<OrderMutationResponse, any>(
        this.baseUrl,
        `/orders_api/api/orders/${orderId}/move`,
        {
          workspaceId,
          sessionId,
          targetQueueId,
        },
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get a single order by ID.
   */
  getOrder(workspaceId: string, sessionId: string, orderId: string): Observable<{ order: Order }> {
    const params: Record<string, string> = {
      workspaceId,
      sessionId,
    };
    return from(
      this.apiService.get<{ order: Order }>(
        this.baseUrl,
        `/orders_api/api/orders/${orderId}`,
        params,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Create a new order (Manual Intake or Form Submission).
   */
  createOrder(
    workspaceId: string,
    sessionId: string,
    orderData: {
      queueId: string;
      summary: string;
      customerIdentifier?: string;
      sourceType?: string;
      sourceId?: string;
      sourceMetadata?: Record<string, string>;
    },
  ): Observable<{ order: Order }> {
    return from(
      this.apiService.post<{ order: Order }, any>(this.baseUrl, `/orders_api/api/orders`, {
        workspaceId,
        sessionId,
        ...orderData,
      }),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * List order copies for the current responder: included in serving policy, status filter (dynamic),
   * sorted by policyQueuePriority, enqueuedAt, id; cursor pagination.
   */
  listMyOrders(
    workspaceId: string,
    sessionId: string,
    limit: number,
    cursor?: string,
    statuses?: string[],
    snoozedOnly?: boolean,
  ): Observable<{
    items: OrderCopyItem[];
    pagination: {
      totalCount: number;
      hasNext: boolean;
      nextCursor: string | null;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number | boolean> = {
      workspaceId,
      sessionId,
      limit,
    };
    if (cursor) params['cursor'] = cursor;
    if (statuses?.length) params['statuses'] = statuses.join(',');
    if (snoozedOnly) params['snoozedOnly'] = true;

    return from(
      this.apiService.get<{
        items: OrderCopyItem[];
        pagination: {
          totalCount: number;
          hasNext: boolean;
          nextCursor: string | null;
          retrievedAt: string;
        };
      }>(this.baseUrl, `/orders_api/api/responders/me/orders`, params),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Search responder orders independent of serving policy.
   * Empty filters should be omitted by callers (wildcard behavior).
   */
  searchMyOrders(
    workspaceId: string,
    sessionId: string,
    limit: number,
    filters?: OrderSearchFilters,
    cursor?: string,
  ): Observable<{
    items: OrderCopyItem[];
    pagination: {
      totalCount: number | null;
      hasNext: boolean;
      nextCursor: string | null;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number | boolean> = {
      workspaceId,
      sessionId,
      limit,
    };
    if (cursor) params['cursor'] = cursor;
    if (filters?.queueId) params['queueId'] = filters.queueId;
    if (filters?.customerName) params['customerName'] = filters.customerName;
    if (filters?.fromDate) params['fromDate'] = filters.fromDate;
    if (filters?.toDate) params['toDate'] = filters.toDate;

    return from(
      this.apiService.get<{
        items: OrderCopyItem[];
        pagination: {
          totalCount: number | null;
          hasNext: boolean;
          nextCursor: string | null;
          retrievedAt: string;
        };
      }>(this.baseUrl, `/orders_api/api/responders/me/orders/search`, params),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Soft-claim the next PENDING order in the responder's serving policy and return mutation payload.
   */
  claimNextOrder(
    workspaceId: string,
    sessionId: string,
  ): Observable<{
    orderId: string | null;
    reason?: string;
    order?: Order;
    orderCopy?: OrderCopyItem;
  }> {
    return from(
      this.apiService.post<
        {
          orderId: string | null;
          reason?: string;
          order?: Order;
          orderCopy?: OrderCopyItem;
        },
        { workspaceId: string; sessionId: string }
      >(this.baseUrl, `/orders_api/api/responders/me/next-order/claim`, {
        workspaceId,
        sessionId,
      }),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Call the next PENDING order in the responder's serving policy (same ordering as claim-next)
   * without soft-claiming.
   */
  callNextOrder(
    workspaceId: string,
    sessionId: string,
  ): Observable<{
    orderId: string | null;
    reason?: string;
    order?: Order;
    orderCopy?: OrderCopyItem;
  }> {
    return from(
      this.apiService.post<
        {
          orderId: string | null;
          reason?: string;
          order?: Order;
          orderCopy?: OrderCopyItem;
        },
        { workspaceId: string; sessionId: string }
      >(this.baseUrl, `/orders_api/api/responders/me/next-order/call`, {
        workspaceId,
        sessionId,
      }),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Set the responder's active serving policy.
   * Pass null for policyId to clear the policy (All queues).
   */
  setActivePolicy(
    workspaceId: string,
    sessionId: string,
    policyId: string | null,
  ): Observable<{
    responderState: {
      activePolicyId?: string | null;
      policyVersion?: number | null;
      orderCopyPolicySyncPending?: boolean;
      [key: string]: unknown;
    };
  }> {
    return from(
      this.apiService.post<{ responderState: any }, any>(
        this.baseUrl,
        `/orders_api/api/responders/me/active-policy`,
        {
          workspaceId,
          sessionId,
          policyId,
        },
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * List responder IDs whose active policy includes the given queue.
   */
  listRespondersServingQueue(
    workspaceId: string,
    sessionId: string,
    queueControlId: string,
  ): Observable<{ responderIds: string[] }> {
    return from(
      this.apiService.get<{ responderIds: string[] }>(
        this.baseUrl,
        `/orders_api/api/responders-serving-queue`,
        { workspaceId, sessionId, queueControlId },
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Create a session-scoped custom serving policy and activate it.
   */
  createCustomServingPolicy(
    workspaceId: string,
    sessionId: string,
    name: string,
    queueRules: Array<{ queueId: string; priority: number }>,
  ): Observable<{
    policy: any;
    responderState?: {
      activePolicyId?: string | null;
      policyVersion?: number | null;
      orderCopyPolicySyncPending?: boolean;
      [key: string]: unknown;
    };
  }> {
    return from(
      this.apiService.post<{ policy: any; responderState?: any }, any>(
        this.baseUrl,
        `/orders_api/api/responders/me/custom-policy`,
        {
          workspaceId,
          sessionId,
          name,
          queueRules: queueRules ?? [],
        },
      ),
    ).pipe(switchMap((obs) => obs));
  }
}
